import { api } from "@/lib/api";

/**
 * 図面 QR の解決結果（API の `DrawingQrResolveResponse` に対応）。
 *
 * **平らな 1 つの形**で返ってくる。現場の回線で往復を増やさないため、案件・明細・
 * 図面・版をまとめて 1 回で受け取る（納期だけは案件と明細の両方にあるので接頭辞で
 * 分かれている）。
 */
export interface ResolvedQr {
  project_id: number;
  project_number: string;
  project_title: string | null;
  project_deadline: string | null;

  project_item_id: number;
  sequence: number;
  drawing_number: string;
  item_name: string;
  material: string | null;
  surface_treatment: string | null;
  quantity: number;
  item_deadline: string | null;

  drawing_id: number;
  /** QR が指す素版。実績の登録にそのまま渡す。 */
  drawing_revision_id: number;
  revision: string;
  page: number;
  /** false なら古い紙。登録は止めず、画面で知らせるだけ。 */
  is_latest_revision: boolean;
  /** サムネイルの取得先（API の固定パス）。生成前・失敗時は null。 */
  thumbnail_path: string | null;
  /** 見積で使われた工程ID（内訳の行順）。候補の先頭へ寄せる。 */
  quotation_process_ids: number[];
}

/**
 * 読み取った QR の値を解決する。
 *
 * **値の形は画面が知らなくてよい。** 読み取った文字列（URL 全体でも、`?` の後ろ
 * だけでも）をそのまま渡せば API が解釈する。形式が変わっても画面を直さずに済む
 * ようにこうしてある。
 */
export function resolveDrawingQr(code: string): Promise<ResolvedQr> {
  return api.get<ResolvedQr>(`/drawings/qr/resolve?code=${encodeURIComponent(code)}`);
}

/** 工作機械（API の `MachineResponse` に対応）。 */
export interface Machine {
  machine_id: number;
  machine_name: string;
  disabled: boolean;
}

interface MachineListResponse {
  items: Machine[];
}

/** 使用中の工作機械を機械名の昇順で取得する（実績入力の選択肢）。 */
export function listActiveMachines(): Promise<Machine[]> {
  return api
    .get<MachineListResponse>("/machines?disabled=false&limit=500")
    .then((res) => res.items);
}

/** 作業実績の登録（API の `WorkLogCreateRequestBody` に対応）。 */
export interface WorkLogInput {
  project_item_id: number;
  process_id: number;
  /** 機械を使わない工程では省く。 */
  machine_id?: number | null;
  /** 時間。API は NUMERIC を文字列で受けるので文字列で送る。 */
  duration: string;
  /** YYYY-MM-DD。省くと API 側で日本時間の今日になる。 */
  work_date?: string;
  /** QR が指す素版。QR を読まずに入れる場合は省く。 */
  drawing_revision_id?: number | null;
}

export interface WorkLog {
  work_log_id: number;
  project_item_id: number;
  drawing_revision_id: number | null;
  work_date: string;
  duration: string;
  process_id: number;
  machine_id: number | null;
  note: string | null;
  created_at: string | null;
}

/** 作業実績を 1 件登録する（作業者はログインユーザー）。 */
export function createWorkLog(input: WorkLogInput): Promise<WorkLog> {
  return api.post<WorkLog>("/work_logs", input);
}

/** 自分が登録した実績 1 件（API の `MyWorkLogResponse` に対応）。 */
export interface MyWorkLog {
  work_log_id: number;
  project_item_id: number;
  project_number: string;
  drawing_number: string;
  process_name: string;
  machine_name: string | null;
  work_date: string;
  duration: string;
  note: string | null;
  created_at: string | null;
}

interface MyWorkLogListResponse {
  items: MyWorkLog[];
}

/** 自分が登録した実績を**登録の新しい順**に取得する（既定 10 件）。 */
export function listMyWorkLogs(limit = 10): Promise<MyWorkLog[]> {
  return api
    .get<MyWorkLogListResponse>(`/work_logs/mine?limit=${limit}`)
    .then((res) => res.items);
}
